// Objektmappen im Risiken-Reiter. Seit 11.10.2026 (zweite Runde) KEIN
// gemeinsamer Abschnitt „Objektmappen" mehr: Jede Objektart ist ein eigener
// Abschnitt des Reiters mit eigenen Mappen —
//   Technische Betriebseinrichtung (EDV/Elektronik, Maschinen stationär,
//     Maschinen mobil sowie selbstfahrende Arbeitsmaschinen)
//   → Betriebsgebäude und Grundstücke mit Photovoltaik → KFZ → Umwelt.
// Die Daten lädt `useObjektmappen` EINMAL im Panel; die Abschnitte zeigen je
// einen Teil davon (`ObjektmappenTeil`).
//
// Elektronik und Maschinen zeigen zusätzlich die Summe aus der Vermögensmatrix:
// Dort steht der ganze Bestand, hier die einzeln erfassten Objekte. Bei den
// Maschinen ist die Differenz die pauschal erfasste Summe. Umwelt zeigt keine
// Objekte, sondern den Überblick über die Anlagen 1a) bis 6) des Fragebogens.
// Gebäude, PV und Kfz sind bewusst NICHT Teil der Betriebsvermögens-Summe
// (siehe VermoegensMatrix) — sie haben eigene Datenquellen und eigene Verträge.
//
// Je Zeile: Bild bzw. Typ-Symbol, Bezeichnung, Eigentümer, Wert, Vertragsstatus
// und die Zähler offener Vorgänge/Schäden. Klick klappt die Mappe auf:
// darunter stehen die Risikoinfos sowie Verträge, Schäden und Vorgänge
// (MappeDetails); von dort geht es in Risiko, Vertrag, Schaden und Vorgang.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { apiFetch } from "../../utils/apiFetch";
import { BRAND } from "../risikoanalyseStep/tokens";
import { Icons } from "./riskIcons";
import { RiskThumb, type RiskThumbKind } from "./RiskThumb";
import { MappeDetails } from "./MappeDetails";
import {
  charOf,
  contactLabel,
  eigentuemerVon,
  eur,
  resolveSignedUrl,
  type MappeRisk,
  type OpenCounts,
} from "./mappeDaten";
import type { NewRiskType } from "../risikoArten";
import { KENNZAHL_MERKMAL, kennzahlFuer, kennzahlZeilen } from "./vermoegensKennzahlen";
import { UmweltUebersicht } from "./UmweltUebersicht";

/**
 * Blockzuordnung wie in der Vermögens-Matrix — Photovoltaik VOR Gebäude, weil
 * eine Dachanlage oft als Immobilien-Risiko erfasst ist.
 *
 * Bewusst KEIN Sammelblock „Sonstige": Elektronik und Maschinen haben seit
 * 11.10.2026 eigene Abschnitte, die ihre Summe aus der Matrix zeigen; alles
 * Übrige steht nur in der Matrix.
 */
const kindOf = (risk: MappeRisk): RiskThumbKind | null => {
  const t = `${risk.riskType ?? ""} ${charOf(risk, "Vermögensart") ?? ""}`.toLowerCase();
  if (t.includes("photovolt") || t.includes("pv-anlage") || t.includes("solar")) return "pv";
  if (t.includes("kfz") || t.includes("fahrzeug")) return "kfz";
  if (
    t.includes("immo") ||
    t.includes("gebäude") ||
    t.includes("gebaeude") ||
    t.includes("grundstück")
  )
    return "gebaeude";
  return null;
};

/** Anzeigename je Objekttyp — Adresse beim Gebäude, Kennzeichen beim Kfz. */
const titleOf = (risk: MappeRisk, kind: RiskThumbKind): string => {
  if (kind === "kfz") {
    const plate = charOf(risk, "licensePlate");
    const marke = [charOf(risk, "manufacturer"), charOf(risk, "model")]
      .filter(Boolean)
      .join(" ");
    return [plate, marke].filter(Boolean).join(" · ") || risk.name || "Fahrzeug";
  }
  if (kind === "gebaeude") {
    const street = charOf(risk, "street", "Straße");
    const plz = charOf(risk, "postalCode", "PLZ");
    const city = charOf(risk, "city", "Ort");
    const adresse = [street, [plz, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    return adresse || risk.name || "Gebäude";
  }
  return risk.name || risk.riskType || "Objekt";
};

/** Zweite Zeile: Typ/Kategorie, damit die Zeile ohne Foto einordbar bleibt. */
const subtitleOf = (risk: MappeRisk, kind: RiskThumbKind): string | null => {
  if (kind === "kfz") return charOf(risk, "vehicleClass");
  if (kind === "gebaeude")
    return [charOf(risk, "typ"), charOf(risk, "kategorie")].filter(Boolean).join(" · ") || null;
  return charOf(risk, "Vermögensart");
};

/** Kennzahlen der Matrix, die die Abschnitte Elektronik und Maschinen summieren. */
const kennzahlenVon = (...ids: string[]): Set<string> =>
  new Set(
    kennzahlZeilen()
      .filter((z) => ids.includes(z.knoten.id))
      .flatMap((z) => z.blaetter),
  );
const ELEKTRONIK = kennzahlenVon("el");
const MASCHINEN_STATIONAER = kennzahlenVon("ms");
const MASCHINEN_MOBIL = kennzahlenVon("m_mobil");

/** Dieselbe Ableitung wie die Matrix — Merkmal „Kennzahl", sonst Vermögensart. */
const kennzahlVon = (risk: MappeRisk): string =>
  kennzahlFuer(
    charOf(risk, KENNZAHL_MERKMAL),
    charOf(risk, "Vermögensart") ?? risk.riskType,
    charOf(risk, "Eigentumsart"),
  );

const wertVon = (risk: MappeRisk): number => {
  const n = Number(risk.versicherungssumme);
  return Number.isFinite(n) ? n : 0;
};

const istUmwelt = (risk: MappeRisk) => (risk.riskType ?? "").toLowerCase() === "umwelt";
const istMaschinenMaske = (risk: MappeRisk) => (risk.riskType ?? "").toLowerCase() === "maschine";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", rowGap: "18px" },
  block: { display: "flex", flexDirection: "column", rowGap: "8px" },
  blockHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    paddingBottom: "6px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  blockIcon: { width: "18px", height: "18px", color: BRAND.gold, display: "inline-flex" },
  blockTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "0.92rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  blockSum: { marginLeft: "auto", fontSize: "0.9rem", fontWeight: 700, color: BRAND.ink },
  plusBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    flexShrink: 0,
    color: BRAND.purple,
    backgroundColor: BRAND.white,
    cursor: "pointer",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("4px"),
    ":hover": { backgroundColor: "#f5eef4", ...shorthands.borderColor(BRAND.purple) },
  },
  /** Summenleiste Elektronik / Maschinen: Tabelle · einzeln · pauschal. */
  summen: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "18px",
    rowGap: "4px",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: "#faf8fb",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    fontSize: "0.8rem",
    color: BRAND.muted,
  },
  summeWert: { fontWeight: 700, color: BRAND.ink, marginLeft: "6px" },
  /** Karte = Zeile + (aufgeklappt) Detailbereich; der Rahmen sitzt außen. */
  card: {
    backgroundColor: BRAND.white,
    overflow: "hidden",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
  cardOpen: { ...shorthands.borderColor(BRAND.purple) },
  row: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    cursor: "pointer",
    backgroundColor: "transparent",
    textAlign: "left",
    width: "100%",
    ...shorthands.border("0"),
    ":hover": { backgroundColor: "#faf7fa" },
  },
  rowDanger: { backgroundColor: "#fdf6f6" },
  chevron: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
    color: BRAND.muted,
    display: "inline-flex",
    transitionProperty: "transform",
    transitionDuration: "140ms",
  },
  chevronOpen: { transform: "rotate(90deg)", color: BRAND.purple },
  hint: { fontSize: "0.72rem", color: BRAND.muted, fontStyle: "italic" },
  rowText: { display: "flex", flexDirection: "column", rowGap: "2px", minWidth: 0, flexGrow: 1 },
  rowTitle: {
    fontSize: "0.88rem",
    fontWeight: 700,
    color: BRAND.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowSub: { fontSize: "0.76rem", color: BRAND.muted },
  rowRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    rowGap: "4px",
    flexShrink: 0,
  },
  wert: { fontSize: "0.9rem", fontWeight: 700, color: BRAND.ink },
  badges: { display: "flex", columnGap: "6px", flexWrap: "wrap", justifyContent: "flex-end" },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    fontSize: "0.72rem",
    fontWeight: 600,
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "7px",
    paddingRight: "7px",
    ...shorthands.borderRadius("999px"),
  },
  badgeOk: { backgroundColor: "#e8f4ec", color: "#1e6b3a" },
  badgeDanger: { backgroundColor: "#fbe9e9", color: "#a32b2b" },
  badgeNeutral: { backgroundColor: "#f1eef3", color: BRAND.muted },
  leer: {
    fontSize: "0.8rem",
    color: BRAND.muted,
    fontStyle: "italic",
    paddingTop: "6px",
    paddingBottom: "6px",
  },
});

export interface ObjektmappenDaten {
  loading: boolean;
  gebaeude: MappeRisk[];
  kfz: MappeRisk[];
  elektronik: MappeRisk[];
  stationaer: MappeRisk[];
  mobil: MappeRisk[];
  umwelt: MappeRisk[];
  thumbs: Record<number, string>;
  counts: OpenCounts;
}

/** Bei den Maschinen sind nur die mit der Maschinen-Maske erfassten Objekte „einzeln". */
const einzeln = (list: MappeRisk[]) => list.filter(istMaschinenMaske);

const summe = (list: MappeRisk[]) => list.reduce((acc, r) => acc + wertVon(r), 0);

/**
 * Lädt die Risikoobjekte der gewählten Firmen EINMAL und teilt sie auf die
 * Abschnitte auf — die Abschnitte sind seit 11.10.2026 getrennt, sollen aber
 * nicht je einen eigenen Request auslösen. Leere Auswahl = nichts laden
 * (Privatkontakt, Immobilienverbund).
 */
export function useObjektmappen(selectedIds: number[]): ObjektmappenDaten {
  const [risks, setRisks] = useState<MappeRisk[]>([]);
  const [thumbs, setThumbs] = useState<Record<number, string>>({});
  const [counts, setCounts] = useState<OpenCounts>({});
  const [loading, setLoading] = useState(false);

  const idsKey = selectedIds.join(",");

  useEffect(() => {
    if (!idsKey) {
      setRisks([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiFetch<MappeRisk[]>(`risk-objects/by-contacts`, { params: { ids: idsKey } })
      .then(({ data }) => {
        if (!cancelled) setRisks(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setRisks([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  // Wie die Matrix nur Objekte der gewählten Firmen — by-contacts findet
  // auch Objekte, bei denen eine gewählte Firma nur Versicherungsnehmer ist.
  const bloecke = useMemo(() => {
    const sel = new Set(idsKey ? idsKey.split(",").map(Number) : []);
    const b = {
      gebaeude: [] as MappeRisk[],
      kfz: [] as MappeRisk[],
      elektronik: [] as MappeRisk[],
      stationaer: [] as MappeRisk[],
      mobil: [] as MappeRisk[],
      umwelt: [] as MappeRisk[],
    };
    for (const r of risks) {
      const owner = r.contactID ?? eigentuemerVon(r)?.id ?? null;
      if (owner != null && !sel.has(owner)) continue;
      if (istUmwelt(r)) {
        b.umwelt.push(r);
        continue;
      }
      const kind = kindOf(r);
      if (kind === "gebaeude" || kind === "pv") b.gebaeude.push(r);
      else if (kind === "kfz") b.kfz.push(r);
      else {
        const kz = kennzahlVon(r);
        if (ELEKTRONIK.has(kz)) b.elektronik.push(r);
        else if (MASCHINEN_MOBIL.has(kz)) b.mobil.push(r);
        else if (MASCHINEN_STATIONAER.has(kz)) b.stationaer.push(r);
      }
    }
    return b;
  }, [risks, idsKey]);

  // Nur die tatsächlich als Mappe gezeigten Objekte anfragen — nicht alle
  // Risiken des Scopes, von denen die meisten Betriebsvermögens-Posten ohne
  // Bild sind.
  const objektIdsKey = useMemo(
    () =>
      [
        ...bloecke.gebaeude,
        ...bloecke.kfz,
        ...bloecke.elektronik,
        ...einzeln(bloecke.stationaer),
        ...einzeln(bloecke.mobil),
      ]
        .map((r) => r.id)
        .sort((a, b) => a - b)
        .join(","),
    [bloecke],
  );

  useEffect(() => {
    if (!objektIdsKey) {
      setThumbs({});
      setCounts({});
      return;
    }
    let cancelled = false;
    apiFetch<Record<number, string>>(`risk-objects/thumbs`, { params: { ids: objektIdsKey } })
      .then(({ data }) => {
        if (cancelled) return;
        const resolved: Record<number, string> = {};
        for (const [riskId, url] of Object.entries(data ?? {})) {
          resolved[Number(riskId)] = resolveSignedUrl(url);
        }
        setThumbs(resolved);
      })
      .catch(() => {
        if (!cancelled) setThumbs({});
      });
    apiFetch<OpenCounts>(`risk-objects/open-counts`, { params: { ids: objektIdsKey } })
      .then(({ data }) => {
        if (!cancelled) setCounts(data ?? {});
      })
      .catch(() => {
        if (!cancelled) setCounts({});
      });
    return () => {
      cancelled = true;
    };
  }, [objektIdsKey]);

  return { loading, ...bloecke, thumbs, counts };
}

/** Füllstand je Abschnitt — steuert, ob er offen oder zugeklappt startet. */
export const objektmappenBefund = (d: ObjektmappenDaten) => ({
  technik: d.elektronik.length + d.stationaer.length + d.mobil.length,
  gebaeude: d.gebaeude.length,
  kfz: d.kfz.length,
  umwelt: d.umwelt.length,
});

/** „+" — Risikoobjekt dieser Art anlegen. Auch im Kopf der Reiter-Abschnitte. */
export function PlusKnopf({ label, onClick }: { label: string; onClick: () => void }) {
  const styles = useStyles();
  return (
    <button
      type="button"
      className={styles.plusBtn}
      title={`${label}: Risikoobjekt hinzufügen`}
      aria-label={`${label}: Risikoobjekt hinzufügen`}
      onClick={onClick}
    >
      <Icons.plus />
    </button>
  );
}

export type ObjektmappenTeilArt = "technik" | "gebaeude" | "kfz" | "umwelt";

export function ObjektmappenTeil({
  teil,
  daten,
  onNeu,
}: {
  teil: ObjektmappenTeilArt;
  daten: ObjektmappenDaten;
  /** „+" im Kopf eines Unterabschnitts (nur Technik): Risikoobjekt anlegen. */
  onNeu?: (typ: NewRiskType) => void;
}) {
  const styles = useStyles();

  /** Genau eine offene Mappe — zwei aufgeklappte Objekte nebeneinander machen
      die Liste unlesbar, und die Detaildaten werden je Mappe nachgeladen. */
  const [expanded, setExpanded] = useState<number | null>(null);
  const { thumbs, counts } = daten;

  if (daten.loading) return <div className={styles.leer}>Objekte werden geladen …</div>;

  const renderKarte = (risk: MappeRisk, kind: RiskThumbKind) => {
    const owner = eigentuemerVon(risk);
    const versichert = !!(risk.contract || risk.contractID);
    const wert = wertVon(risk);
    const c = counts[risk.id];
    const sub = subtitleOf(risk, kind);
    const open = expanded === risk.id;
    return (
      <div key={risk.id} className={mergeClasses(styles.card, open && styles.cardOpen)}>
        <button
          type="button"
          className={mergeClasses(styles.row, !versichert && styles.rowDanger)}
          aria-expanded={open}
          onClick={() => setExpanded((prev) => (prev === risk.id ? null : risk.id))}
        >
          <span className={mergeClasses(styles.chevron, open && styles.chevronOpen)} aria-hidden>
            <Icons.arrow />
          </span>
          <RiskThumb
            kind={kind}
            photoUrl={thumbs[risk.id]}
            vehicleClass={charOf(risk, "vehicleClass")}
            manufacturer={charOf(risk, "manufacturer")}
            alt={titleOf(risk, kind)}
          />
          <span className={styles.rowText}>
            <span className={styles.rowTitle}>{titleOf(risk, kind)}</span>
            {sub && <span className={styles.rowSub}>{sub}</span>}
            <span className={styles.rowSub}>Eigentümer: {contactLabel(owner)}</span>
            {!open && (
              <span className={styles.hint}>
                Detaillierte Risikoinfos, Verträge, Schäden und Vorgänge zum Aufklappen
              </span>
            )}
          </span>
          <span className={styles.rowRight}>
            <span className={styles.wert}>{wert > 0 ? eur(wert) : "—"}</span>
            <span className={styles.badges}>
              {versichert ? (
                <span className={mergeClasses(styles.badge, styles.badgeOk)}>
                  ✓ {risk.contract?.branch ?? "Vertrag"}
                  {risk.contract?.contractNbr ? ` · ${risk.contract.contractNbr}` : ""}
                </span>
              ) : (
                <span className={mergeClasses(styles.badge, styles.badgeDanger)}>
                  ⚠ ohne Vertrag
                </span>
              )}
              {!!c?.openSchaeden && (
                <span className={mergeClasses(styles.badge, styles.badgeDanger)}>
                  {c.openSchaeden} offene{c.openSchaeden === 1 ? "r" : ""} Schaden
                  {c.openSchaeden === 1 ? "" : "sfälle"}
                </span>
              )}
              {!!c?.openVorgaenge && (
                <span className={mergeClasses(styles.badge, styles.badgeNeutral)}>
                  {c.openVorgaenge} offene{c.openVorgaenge === 1 ? "r" : ""} Vorgang
                  {c.openVorgaenge === 1 ? "" : "e"}
                </span>
              )}
            </span>
          </span>
        </button>
        {open && (
          <MappeDetails
            riskId={risk.id}
            characteristics={risk.characteristics}
            versicherungssumme={wert > 0 ? wert : null}
          />
        )}
      </div>
    );
  };

  const leer = <div className={styles.leer}>Für diese Auswahl ist nichts erfasst.</div>;

  const karten = (list: MappeRisk[], kind: RiskThumbKind) =>
    list.length === 0 ? leer : list.map((r) => renderKarte(r, kind));

  if (teil === "gebaeude")
    return (
      <div className={styles.root}>
        {daten.gebaeude.length === 0
          ? leer
          : daten.gebaeude.map((r) => renderKarte(r, kindOf(r) ?? "gebaeude"))}
      </div>
    );
  if (teil === "kfz") return <div className={styles.root}>{karten(daten.kfz, "kfz")}</div>;
  if (teil === "umwelt") return <UmweltUebersicht risks={daten.umwelt} />;

  // Technische Betriebseinrichtung: drei Unterabschnitte mit je eigenem „+".
  const unterKopf = (label: string, icon: ReactNode, wert: number, neu: NewRiskType) => (
    <div className={styles.blockHead}>
      <span className={styles.blockIcon}>{icon}</span>
      <span className={styles.blockTitle}>{label}</span>
      <span className={styles.blockSum}>{wert > 0 ? eur(wert) : ""}</span>
      {onNeu && <PlusKnopf label={label} onClick={() => onNeu(neu)} />}
    </div>
  );

  /** Maschinen: Summe aus der Matrix, davon einzeln über die Maschinen-Maske
      erfasst, der Rest ist pauschal. Mappen nur für die einzeln erfassten. */
  const maschinen = (key: string, label: string, list: MappeRisk[]) => {
    const einzelne = einzeln(list);
    const gesamt = summe(list);
    const summeEinzeln = summe(einzelne);
    return (
      <div key={key} className={styles.block}>
        {unterKopf(label, <Icons.factory />, gesamt, "machine")}
        <div className={styles.summen}>
          <span>
            Summe gesamt (aus der Tabelle)
            <span className={styles.summeWert}>{eur(gesamt)}</span>
          </span>
          <span>
            davon einzeln erfasst
            <span className={styles.summeWert}>{eur(summeEinzeln)}</span>
          </span>
          <span>
            pauschal
            <span className={styles.summeWert}>{eur(gesamt - summeEinzeln)}</span>
          </span>
        </div>
        {karten(einzelne, "sonstiges")}
      </div>
    );
  };

  const summeElektronik = summe(daten.elektronik);
  return (
    <div className={styles.root}>
      {/* Elektronik: Für einzelne Elektronik-Objekte gibt es noch keine eigene
          Maske — deshalb nur die Summe aus der Matrix und die Positionen. */}
      <div className={styles.block}>
        {unterKopf("EDV, Elektronik", <Icons.cyber />, summeElektronik, "elektronik")}
        <div className={styles.summen}>
          <span>
            Summe gesamt (aus der Tabelle)
            <span className={styles.summeWert}>{eur(summeElektronik)}</span>
          </span>
        </div>
        {karten(daten.elektronik, "sonstiges")}
      </div>
      {maschinen("stationaer", "Maschinen stationär", daten.stationaer)}
      {maschinen(
        "mobil",
        "Maschinen mobil sowie selbstfahrende Arbeitsmaschinen",
        daten.mobil,
      )}
    </div>
  );
}
